import React, { useState } from 'react';
import { bool, object } from 'prop-types';
import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import { LINE_ITEM_MERCHANT_FEE, propTypes } from '../../util/types';
import {
  PROCESSING_FEE_DOMESTIC_RATE,
  PROCESSING_FEE_INTERNATIONAL_RATE,
  PROCESSING_FEE_FIXED_CENTS,
} from '../../util/feeConstants';

import classNames from 'classnames';
import IconArrowHead from '../IconArrowHead/IconArrowHead';
import css from './OrderBreakdown.module.css';

const { Money } = sdkTypes;

const isValidFee = lineItem => {
  return lineItem.lineTotal instanceof Money && lineItem.lineTotal.amount <= 0;
};

const LineItemMerchantFeeMaybe = props => {
  const { lineItems, isProvider, transaction, intl } = props;
  const [detailsOpen, setDetailsOpen] = useState(false);

  const merchantFeeLineItem = lineItems.find(
    item => item.code === LINE_ITEM_MERCHANT_FEE && !item.reversal
  );

  if (!isProvider || !merchantFeeLineItem) {
    return null;
  }

  if (!isValidFee(merchantFeeLineItem)) {
    // eslint-disable-next-line no-console
    console.error('invalid merchant fee line item:', merchantFeeLineItem);
    throw new Error('Merchant fee should be present and the value should be zero or negative');
  }

  const fee = merchantFeeLineItem.lineTotal;
  const formattedFee = fee ? formatMoney(intl, fee) : null;
  const feeBreakdown = transaction?.attributes?.protectedData?.feeBreakdown;
  const hasBreakdown = feeBreakdown != null;
  const isUSBuyer = feeBreakdown?.isUSBuyer;

  // Derive breakdown amounts from the total fee and known rates
  let baseRateAmount = null;
  let intlSurchargeAmount = null;
  const fixedFeeCents = PROCESSING_FEE_FIXED_CENTS;

  if (hasBreakdown && fee) {
    const totalFeeCents = Math.abs(fee.amount);
    // totalFeeCents = base rate + international surcharge (if intl) + the fixed fee
    // (util/feeConstants.js), so the percentage portion is what is left after the fixed fee
    const percentagePortion = totalFeeCents - fixedFeeCents;

    if (isUSBuyer) {
      // All percentage portion is base rate
      baseRateAmount = new Money(-percentagePortion, fee.currency);
    } else {
      // The percentage portion is the international rate: split it into the base rate and the
      // surcharge in proportion to the rates
      const baseShare = PROCESSING_FEE_DOMESTIC_RATE / PROCESSING_FEE_INTERNATIONAL_RATE;
      const baseRateCents = Math.round(baseShare * percentagePortion);
      const intlSurchargeCents = percentagePortion - baseRateCents;
      baseRateAmount = new Money(-baseRateCents, fee.currency);
      intlSurchargeAmount = new Money(-intlSurchargeCents, fee.currency);
    }
  }

  const fixedFeeFormatted = formatMoney(intl, new Money(-fixedFeeCents, fee.currency));

  return (
    <div className={classNames(css.lineItem, css.feeWrapper)}>
      <div className={css.lineItem}>
        <span className={css.itemLabel}>
          {hasBreakdown ? (
            <span
              className={css.feeToggle}
              onClick={() => setDetailsOpen(!detailsOpen)}
              role="button"
              tabIndex={0}
              aria-expanded={detailsOpen}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setDetailsOpen(!detailsOpen);
                }
              }}
            >
              <FormattedMessage id="OrderBreakdown.merchantFee" />
              <span
                className={`${css.feeInfoIcon} ${detailsOpen ? css.feeInfoIconOpen : ''}`}
                aria-hidden="true"
              >
                i
              </span>
              <IconArrowHead
                direction={detailsOpen ? 'down' : 'right'}
                size="small"
                className={classNames(css.feeChevron, { [css.feeChevronOpen]: detailsOpen })}
              />
            </span>
          ) : (
            <FormattedMessage id="OrderBreakdown.merchantFee" />
          )}
        </span>
        <span className={css.itemValue}>{formattedFee}</span>
      </div>

      {hasBreakdown && (
        <p className={css.feeCardType}>
          <FormattedMessage
            id={isUSBuyer ? 'OrderBreakdown.merchantFeeDomestic' : 'OrderBreakdown.merchantFeeInternational'}
          />
        </p>
      )}

      {hasBreakdown && detailsOpen && (
        <div className={css.feeSubRows}>
          <div className={css.feeSubRow}>
            <span className={css.feeSubLabel}>
              <FormattedMessage
                id="OrderBreakdown.merchantFeeBaseRate"
                values={{
                  percentage: intl.formatNumber(PROCESSING_FEE_DOMESTIC_RATE * 100, {
                    maximumFractionDigits: 2,
                  }),
                }}
              />
            </span>
            <span className={css.feeSubValue}>
              {baseRateAmount ? formatMoney(intl, baseRateAmount) : null}
            </span>
          </div>
          {!isUSBuyer && intlSurchargeAmount && (
            <div className={css.feeSubRow}>
              <span className={css.feeSubLabel}>
                <FormattedMessage
                  id="OrderBreakdown.merchantFeeIntlSurcharge"
                  values={{
                    percentage: intl.formatNumber(
                      (PROCESSING_FEE_INTERNATIONAL_RATE - PROCESSING_FEE_DOMESTIC_RATE) * 100,
                      { maximumFractionDigits: 2 }
                    ),
                  }}
                />
              </span>
              <span className={css.feeSubValue}>
                {formatMoney(intl, intlSurchargeAmount)}
              </span>
            </div>
          )}
          <div className={css.feeSubRow}>
            <span className={css.feeSubLabel}>
              <FormattedMessage id="OrderBreakdown.merchantFeeFixed" />
            </span>
            <span className={css.feeSubValue}>{fixedFeeFormatted}</span>
          </div>
        </div>
      )}

      {hasBreakdown && (
        <p className={css.feePassthrough}>
          <FormattedMessage id="OrderBreakdown.merchantFeePassthrough" />
        </p>
      )}
    </div>
  );
};

LineItemMerchantFeeMaybe.propTypes = {
  lineItems: propTypes.lineItems.isRequired,
  isProvider: bool.isRequired,
  transaction: object,
  intl: intlShape.isRequired,
};

export default LineItemMerchantFeeMaybe;
